<template>
  <div class="dashboard">
    <div class="toolbar">
      <h3>数据概览</h3>
      <div class="toolbar-actions">
        <el-radio-group v-model="range" size="small" @change="loadDashboard">
          <el-radio-button label="today">今日</el-radio-button>
          <el-radio-button label="7d">近7天</el-radio-button>
          <el-radio-button label="30d">近30天</el-radio-button>
          <el-radio-button label="month">本月</el-radio-button>
        </el-radio-group>
        <el-button size="small" :loading="loading" @click="loadDashboard">刷新</el-button>
      </div>
    </div>

    <el-row v-loading="loading" :gutter="16" class="kpi-row">
      <el-col v-for="card in kpiCards" :key="card.key" :xs="12" :sm="8" :md="6" :lg="4">
        <el-card shadow="hover" class="kpi-card">
          <div class="kpi-label">{{ card.label }}</div>
          <div class="kpi-value">{{ card.value }}</div>
          <div v-if="card.hint" class="kpi-hint">{{ card.hint }}</div>
        </el-card>
      </el-col>
    </el-row>

    <el-card class="section-card revenue-card" shadow="never">
      <div slot="header" class="card-header">
        <span>营收（内测）</span>
        <el-tag size="mini" type="info">支付未上线</el-tag>
      </div>
      <el-row :gutter="24">
        <el-col :span="8">
          <div class="revenue-item">
            <div class="revenue-label">MRR</div>
            <div class="revenue-value">¥0</div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="revenue-item">
            <div class="revenue-label">付费用户</div>
            <div class="revenue-value">0</div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="revenue-item">
            <div class="revenue-label">说明</div>
            <div class="revenue-message">{{ revenueMessage }}</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <el-row :gutter="16" class="chart-row">
      <el-col :span="24">
        <el-card shadow="never" class="section-card">
          <div slot="header">趋势</div>
          <div ref="trendChart" class="chart-box" />
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row">
      <el-col :xs="24" :md="12">
        <el-card shadow="never" class="section-card">
          <div slot="header">用户档位分布</div>
          <div ref="planChart" class="chart-box chart-box-sm" />
        </el-card>
      </el-col>
      <el-col :xs="24" :md="12">
        <el-card shadow="never" class="section-card">
          <div slot="header">任务状态分布（全量）</div>
          <div ref="statusChart" class="chart-box chart-box-sm" />
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts/core';
import { LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { fetchAdminDashboard } from '../api/admin';

echarts.use([
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

const PLAN_LABELS = {
  BETA_FREE: '内测免费',
  FREE: '免费',
  PRO: 'Pro',
  PRO_PLUS: 'Pro+',
  TEAM: '团队',
};

const STATUS_LABELS = {
  PENDING: '等待中',
  RUNNING: '进行中',
  SUCCESS: '成功',
  FAILED: '失败',
  CANCELLED: '已取消',
};

const STATUS_COLORS = {
  PENDING: '#94a3b8',
  RUNNING: '#3b82f6',
  SUCCESS: '#22c55e',
  FAILED: '#ef4444',
  CANCELLED: '#a78bfa',
};

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      range: '7d',
      data: null,
      trendChart: null,
      planChart: null,
      statusChart: null,
    };
  },
  computed: {
    revenueMessage() {
      return (this.data && this.data.revenue && this.data.revenue.message)
        || '内测阶段免费，支付功能尚未上线';
    },
    kpiCards() {
      const k = (this.data && this.data.kpis) || {};
      return [
        { key: 'totalUsers', label: '累计注册', value: this.fmtNum(k.totalUsers), hint: `已验证 ${this.fmtNum(k.verifiedUsers)}` },
        { key: 'newUsers', label: '新增注册', value: this.fmtNum(k.newUsers), hint: this.rangeHint },
        { key: 'activeUsers', label: '活跃用户', value: this.fmtNum(k.activeUsers), hint: '切片或上传去重' },
        { key: 'sliceTasks', label: '切片任务', value: this.fmtNum(k.sliceTasks), hint: `累计 ${this.fmtNum(k.totalSliceTasks)}` },
        { key: 'sliceSuccessRate', label: '任务成功率', value: this.fmtPercent(k.sliceSuccessRate), hint: this.rangeHint },
        { key: 'runningTasks', label: '进行中', value: this.fmtNum(k.runningTasks), hint: '等待 + 运行' },
        { key: 'workerMaxConcurrency', label: 'Worker 并发', value: this.fmtNum(k.workerMaxConcurrency), hint: 'FFmpeg 上限' },
        { key: 'uploadBytes', label: '上传流量', value: this.fmtBytes(k.uploadBytes), hint: this.rangeHint },
        { key: 'feedbackCount', label: '用户反馈', value: this.fmtNum(k.feedbackCount), hint: this.rangeHint },
        { key: 'inviteCodesUsed', label: '邀请码已用', value: this.fmtNum(k.inviteCodesUsed), hint: '全量累计' },
      ];
    },
    rangeHint() {
      const map = { today: '今日', '7d': '近7天', '30d': '近30天', month: '本月' };
      return map[this.range] || '';
    },
  },
  mounted() {
    this.loadDashboard();
    this._onResize = () => this.resizeCharts();
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    this.disposeCharts();
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const res = await fetchAdminDashboard(this.range);
        this.data = res;
        this.$nextTick(() => this.renderCharts());
      } catch (e) {
        this.$message.error((e && e.message) || '加载数据概览失败');
      } finally {
        this.loading = false;
      }
    },
    renderCharts() {
      if (!this.data) return;
      this.renderTrendChart();
      this.renderPlanChart();
      this.renderStatusChart();
    },
    renderTrendChart() {
      const el = this.$refs.trendChart;
      if (!el) return;
      if (!this.trendChart) {
        this.trendChart = echarts.init(el);
      }
      const t = this.data.trends || {};
      this.trendChart.setOption({
        color: ['#3b82f6', '#22c55e', '#f59e0b'],
        tooltip: { trigger: 'axis' },
        legend: { data: ['新增注册', '活跃用户', '切片任务'], bottom: 0 },
        grid: { left: 48, right: 24, top: 24, bottom: 48 },
        xAxis: { type: 'category', data: t.dates || [], boundaryGap: false },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          { name: '新增注册', type: 'line', smooth: true, data: t.newUsers || [] },
          { name: '活跃用户', type: 'line', smooth: true, data: t.activeUsers || [] },
          { name: '切片任务', type: 'line', smooth: true, data: t.sliceTasks || [] },
        ],
      }, true);
    },
    renderPlanChart() {
      const el = this.$refs.planChart;
      if (!el) return;
      if (!this.planChart) {
        this.planChart = echarts.init(el);
      }
      const map = (this.data.breakdown && this.data.breakdown.usersByPlan) || {};
      const seriesData = Object.keys(map).map((key) => ({
        name: PLAN_LABELS[key] || key,
        value: map[key],
      }));
      this.planChart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0, type: 'scroll' },
        series: [{
          type: 'pie',
          radius: ['38%', '68%'],
          center: ['50%', '45%'],
          data: seriesData,
          label: { formatter: '{b}\n{c}' },
        }],
      }, true);
    },
    renderStatusChart() {
      const el = this.$refs.statusChart;
      if (!el) return;
      if (!this.statusChart) {
        this.statusChart = echarts.init(el);
      }
      const map = (this.data.breakdown && this.data.breakdown.tasksByStatus) || {};
      const seriesData = Object.keys(map).map((key) => ({
        name: STATUS_LABELS[key] || key,
        value: map[key],
        itemStyle: { color: STATUS_COLORS[key] },
      }));
      this.statusChart.setOption({
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0, type: 'scroll' },
        series: [{
          type: 'pie',
          radius: ['38%', '68%'],
          center: ['50%', '45%'],
          data: seriesData,
          label: { formatter: '{b}\n{c}' },
        }],
      }, true);
    },
    resizeCharts() {
      [this.trendChart, this.planChart, this.statusChart].forEach((c) => {
        if (c) c.resize();
      });
    },
    disposeCharts() {
      [this.trendChart, this.planChart, this.statusChart].forEach((c) => {
        if (c) c.dispose();
      });
      this.trendChart = null;
      this.planChart = null;
      this.statusChart = null;
    },
    fmtNum(v) {
      if (v == null || Number.isNaN(Number(v))) return '—';
      return Number(v).toLocaleString('zh-CN');
    },
    fmtPercent(v) {
      if (v == null || Number.isNaN(Number(v))) return '—';
      return `${(Number(v) * 100).toFixed(1)}%`;
    },
    fmtBytes(bytes) {
      if (bytes == null || Number(bytes) === 0) return '0 B';
      const n = Number(bytes);
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let i = 0;
      let val = n;
      while (val >= 1024 && i < units.length - 1) {
        val /= 1024;
        i += 1;
      }
      return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    },
  },
};
</script>

<style scoped>
.dashboard {
  max-width: 1400px;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.toolbar h3 {
  margin: 0;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.kpi-row {
  margin-bottom: 8px;
}
.kpi-card {
  margin-bottom: 16px;
}
.kpi-label {
  font-size: 13px;
  color: #6b7280;
}
.kpi-value {
  margin-top: 8px;
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}
.kpi-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #9ca3af;
}
.section-card {
  margin-bottom: 16px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.revenue-item {
  padding: 4px 0;
}
.revenue-label {
  font-size: 13px;
  color: #6b7280;
}
.revenue-value {
  margin-top: 6px;
  font-size: 22px;
  font-weight: 600;
  color: #9ca3af;
}
.revenue-message {
  margin-top: 6px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}
.chart-box {
  height: 320px;
}
.chart-box-sm {
  height: 300px;
}
</style>
